import { useState } from 'react'
import { Autocomplete, Avatar, Backdrop, Box, Button, CircularProgress, Dialog, Drawer, Grid, IconButton, Link, TextField, Typography } from '@mui/material'
import { Twitter } from '@mui/icons-material'

import MyPageEditor from 'component/MyPage/MyPageEditor'
import GameHistory from 'component/MyPage/GameHistory'
import WinRateByDeckType from 'component/MyPage/WinRateByDeckType'
import RateTransition from 'component/MyPage/RateTransition'

import { User } from 'model/User'
import { Game } from 'model/Game'

import { useMediaContext } from 'context/MediaContext'

import { db } from "../../firebase"
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import * as userRepository from 'repository/UserRepository'
import { Season } from 'model/Season'

type Props = {
  isSubscriber: boolean
  isOwnPage: boolean
  user: User
  seasons: Season[]
  season: Season
  games: Game[]
  setShowing: (val: 'History' | 'Record') => void
  setGamesSpecificDeckType: (games: Game[]) => void
  updateUser: (user: User) => void
  changeSeason: (season: Season) => void
}
export default function MyPageTop(props: Props) {
  const { isPc } = useMediaContext()
  const [isLoading, setIsLoading] = useState(false)

  const [isEditing, setIsEditing] = useState(false)
  function handleSave(user: User) {
    props.updateUser(user)
    setIsEditing(false)
  }

  function getMasterDuelId(): string {
    if (!props.user.master_duel_id) return ''
    return `${props.user.master_duel_id?.slice(0, 3)}-${props.user.master_duel_id?.slice(3,6)}-${props.user.master_duel_id?.slice(6,9)}`
  }

  async function handleSubscribe() {
    setIsLoading(true)
    const unsub = userRepository.watchList(users => {
        unsub()
        window.location.assign(users[0].billing_portal_url!)
      },
      [{ key: 'uuid', condition: '==', value: props.user.uuid }],
      undefined,
      1,
      ['modified']
    )
    const customerDoc = doc(db, 'customer', props.user.uuid)
    const customerDocRef = await getDoc(customerDoc)
    updateDoc(customerDoc, {
      issue_request: (customerDocRef.data()?.issue_request ?? 0) + 1
    })
  }

  return (
    <Box>
      <Box sx={{
        backgroundColor: 'background.default',
        mx: isPc ? 10 : 0,
        mb: 2,
      }}>
        <Grid container>
          <Grid item xs={8} display='flex' flexDirection='row' alignItems='flex-end'>
            <Avatar
              src={props.user.profile_image_url}
              sx={isPc ? { width: 120, height: 120 } : {width: 80, height: 80}}
            />
            {isPc &&
              <Box display='flex' flexDirection='column' sx={{ml: 0.5}}>
                <Box display='flex' flexDirection='row' sx={{mb: 2}}>
                  <Typography variant='h6' pt={0.5}>{props.user.name}</Typography>
                  <IconButton
                    color='primary'
                    disabled={!(props.user.twitter_id)}
                    href={`https://twitter.com/${props.user.twitter_id}`}
                    target='_blank'
                  >
                    <Twitter />
                  </IconButton>
                </Box>
                <Box display='flex' flexDirection='row' textAlign='center'>
                  <Box sx={{mr: 1}}>
                    <Typography variant='body2' color='text.secondary'>レート</Typography>
                    <Typography variant='body2'>{props.user.rate}</Typography>
                  </Box>
                  <Box>
                    <Typography variant='body2' color='text.secondary'>Master Duel ID</Typography>
                    <Typography variant='body2'>
                      {getMasterDuelId()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            }
            {!isPc &&
              <>
                <IconButton
                  color='primary'
                  disabled={!(props.user.twitter_id)}
                  href={`https://twitter.com/${props.user.twitter_id}`}
                  target='_blank'
                >
                  <Twitter />
                </IconButton>
              </>
            }
          </Grid>
          <Grid item xs={4} display='flex' justifyContent='flex-end' alignItems='center'>
            {props.isOwnPage &&
              <Box>
                <Button variant='outlined' color='inherit' size={isPc ? 'large' : 'small'} onClick={() => setIsEditing(true)}>
                  {isPc ? 'プロフィールを編集' : '編集'}
                </Button>
              </Box>
            }
          </Grid>
          {!isPc &&
            <>
              <Grid item xs={12}>
                <Typography>{props.user.name}</Typography>
              </Grid>
              <Grid item xs={12} display='flex' flexDirection='row' textAlign='center' sx={{mt: 1}}>
                <Box sx={{mr: 1}}>
                  <Typography variant='body2' color='text.secondary'>レート</Typography>
                  <Typography variant='body2'>{props.user.rate}</Typography>
                </Box>
                <Box>
                  <Typography variant='body2' color='text.secondary'>Master Duel ID</Typography>
                  <Typography variant='body2'>
                    {getMasterDuelId()}
                  </Typography>
                </Box>
              </Grid>
            </>
          }
        </Grid>
        {props.isOwnPage && props.user.is_subscriber &&
          <Box sx={{mt: isPc ? 1 : 0.5}}>
            <Link
              variant='body2'
              sx={{ textDecoration: 'none', cursor: 'pointer' }}
              onClick={handleSubscribe}
            >
              サブスクリプションを管理
            </Link>
          </Box>
        }
      </Box>

      <Autocomplete
        options={props.seasons}
        getOptionLabel={op => op.name}
        isOptionEqualToValue={(op, val) => op.id === val.id}
        renderInput={(params) =>
          <TextField
            {...params}
            variant='standard'
            required
          />}
        value={props.season}
        onChange={(e, v) => props.changeSeason(v!)}
        sx={{
          mx: isPc ? 10 : 0,
          mb: 2
        }}
      />

      <Box sx={{
        border: '1px solid rgba(255, 255, 255, 0.12)',
        backgroundColor: 'background.default',
        borderRadius: 1,
        mx: isPc ? 10 : 0,
        mb: 2,
        px: 2,
        pt: 2,
        pb: 1,
      }}>
        <Typography>
          対戦履歴
        </Typography>
        {(props.isOwnPage || props.user.open_game_history) && props.games.length > 0 &&
          <>
            <GameHistory
              isSubscriber={props.isSubscriber}
              isOwnPage={props.isOwnPage}
              user={props.user}
              games={[props.games[0]]}
            />
            <Typography textAlign='end' variant='body2' sx={{mt: 1}}>
              <Link
                onClick={() => props.setShowing('History')}
                sx={{cursor: 'pointer'}}
              >
                もっと見る
              </Link>
            </Typography>
          </>
        }
        {(props.isOwnPage || props.user.open_game_history) && props.games.length === 0 &&
          <Typography variant='body2' color='text.secondary'>対戦記録がありません。</Typography>
        }
        {!props.isOwnPage && !props.user.open_game_history &&
          <Typography variant='body2' color='text.secondary'>対戦履歴は非公開です。</Typography>
        }
      </Box>

      <Box sx={{
        border: '1px solid rgba(255, 255, 255, 0.12)',
        backgroundColor: 'background.default',
        borderRadius: 1,
        mx: isPc ? 10 : 0,
        mb: 2,
        px: 2,
        pt: 2,
        pb: 1
      }}>
        <Typography>デッキごとの勝率</Typography>
        {(props.isOwnPage || props.user.open_winrate_by_decktype) && props.games.length > 0 &&
          <WinRateByDeckType user={props.user} games={props.games} showDetail={(games: Game[]) => {
            props.setShowing('Record')
            props.setGamesSpecificDeckType(games)
          }} />
        }
        {(props.isOwnPage || props.user.open_winrate_by_decktype) && props.games.length === 0 &&
          <Typography variant='body2' color='text.secondary'>対戦記録がありません。</Typography>
        }
        {!props.isOwnPage && !props.user.open_winrate_by_decktype &&
          <Typography variant='body2' color='text.secondary'>デッキごとの勝率は非公開です。</Typography>
        }
      </Box>

      <Box sx={{
        border: '1px solid rgba(255, 255, 255, 0.12)',
        backgroundColor: 'background.default',
        borderRadius: 1,
        mx: isPc ? 10 : 0,
        mb: 2,
        px: 2,
        pt: 2,
        pb: 1
      }}>
        <Typography>レート推移</Typography>
        {(props.isOwnPage || props.user.open_rate_transition) && props.games.length > 0 &&
          <RateTransition user={props.user} games={props.games} />
        }
        {(props.isOwnPage || props.user.open_rate_transition) && props.games.length === 0 &&
          <Typography variant='body2' color='text.secondary'>対戦記録がありません。</Typography>
        }
        {!props.isOwnPage && !props.user.open_rate_transition &&
          <Typography variant='body2' color='text.secondary'>レート推移は非公開です。</Typography>
        }
      </Box>

      {isPc &&
        <Dialog open={isEditing} sx={{}}>
          <Box sx={{height: '80vh', bgcolor: 'background.default'}}>
            <MyPageEditor
              user={props.user}
              handleSave={handleSave}
              handleCancel={() => setIsEditing(false)}
            />
          </Box>
        </Dialog>
      }

      {!isPc &&        
        <Drawer anchor='bottom' open={isEditing}>
          <Box sx={{ width: '100vw', height: '95vh', bgcolor: 'background.default'}}>
            <MyPageEditor
              user={props.user}
              handleSave={handleSave}
              handleCancel={() => setIsEditing(false)}
            />
          </Box>
        </Drawer>
      }
      <Backdrop open={isLoading}>
        <CircularProgress />
      </Backdrop>
    </Box>
  )
}